import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, XCircle, Upload, Download, Trash2,
  Loader2, FileText, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUT_OPTIONS = [
  { value: "valide", label: "Validé", icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/30" },
  { value: "en_cours", label: "En cours", icon: AlertCircle, color: "text-orange-400", bg: "bg-orange-500/15 border-orange-500/30" },
  { value: "non_valide", label: "Non validé", icon: XCircle, color: "text-red-400", bg: "bg-red-500/15 border-red-500/30" },
];

function getGlobalStatus(conditions) {
  if (!conditions || conditions.length === 0) return null;
  const statuts = conditions.map(c => c.statut);
  if (statuts.every(s => s === "valide")) return { label: "Chantier prêt à démarrer", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle2 };
  if (statuts.some(s => s === "non_valide")) return { label: "Conditions non remplies", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", icon: XCircle };
  return { label: "Démarrage partiel", color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30", icon: AlertCircle };
}

export function ConditionsChantierPanel({ projetId }) {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});
  const debounceTimers = useRef({});

  const loadConditions = useCallback(async () => {
    try {
      const res = await api.get(`/conditions-chantier/${projetId}`);
      setConditions(res.data);
    } catch (e) {
      // silently ignore if project not found yet
    } finally {
      setLoading(false);
    }
  }, [projetId]);

  useEffect(() => { loadConditions(); }, [loadConditions]);

  const updateCondition = async (conditionId, field, value) => {
    setConditions(prev => prev.map(c =>
      c.condition_id === conditionId ? { ...c, [field]: value } : c
    ));

    // Debounce commentaire saves
    if (field === "commentaire") {
      if (debounceTimers.current[conditionId]) clearTimeout(debounceTimers.current[conditionId]);
      debounceTimers.current[conditionId] = setTimeout(() => {
        saveCondition(conditionId, { [field]: value });
      }, 1000);
    } else {
      await saveCondition(conditionId, { [field]: value });
    }
  };

  const saveCondition = async (conditionId, data) => {
    setSaving(conditionId);
    try {
      const res = await api.put(`/conditions-chantier/${projetId}/${conditionId}`, data);
      setConditions(prev => prev.map(c =>
        c.condition_id === conditionId ? res.data : c
      ));
    } catch (e) {
      toast.error("Erreur sauvegarde condition");
    } finally {
      setSaving(null);
    }
  };

  const handleUpload = async (conditionId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    setUploading(conditionId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/conditions-chantier/${projetId}/${conditionId}/document`, formData);
      setConditions(prev => prev.map(c =>
        c.condition_id === conditionId ? res.data : c
      ));
      toast.success("Document uploadé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteDoc = async (conditionId) => {
    try {
      await api.delete(`/conditions-chantier/${projetId}/${conditionId}/document`);
      setConditions(prev => prev.map(c =>
        c.condition_id === conditionId ? { ...c, document_url: null } : c
      ));
      toast.success("Document supprimé");
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-6 text-zinc-500">
      <Loader2 size={20} className="animate-spin mr-2" /> Chargement des conditions...
    </div>
  );

  const globalStatus = getGlobalStatus(conditions);

  return (
    <div className="space-y-3" data-testid="conditions-chantier-panel">
      {/* Global indicator + toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors ${globalStatus?.bg || "bg-zinc-900 border-zinc-800"}`}
        data-testid="conditions-global-indicator"
      >
        {globalStatus && <globalStatus.icon size={20} className={globalStatus.color} />}
        <span className={`text-sm font-semibold flex-1 text-left ${globalStatus?.color || "text-zinc-400"}`}>
          {globalStatus?.label || "Conditions de démarrage"}
        </span>
        <span className="text-zinc-500 text-xs">
          {conditions.filter(c => c.statut === "valide").length}/{conditions.length} validées
        </span>
        {expanded ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
      </button>

      {/* Expanded conditions list */}
      {expanded && (
        <div className="space-y-2 animate-fade-in" data-testid="conditions-list">
          {conditions.map(c => {
            const opt = STATUT_OPTIONS.find(o => o.value === c.statut) || STATUT_OPTIONS[2];
            return (
              <div key={c.condition_id} className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid={`condition-${c.condition_id}`}>
                <div className="flex items-start gap-3">
                  <span className="text-zinc-300 text-sm flex-1 pt-0.5">{c.label}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    {STATUT_OPTIONS.map(o => (
                      <button
                        key={o.value}
                        onClick={() => updateCondition(c.condition_id, "statut", o.value)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                          c.statut === o.value ? o.bg : "border-transparent text-zinc-600 hover:text-zinc-400"
                        }`}
                        data-testid={`condition-${c.condition_id}-${o.value}`}
                      >
                        <o.icon size={12} />
                        <span className="hidden sm:inline">{o.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 items-center">
                  <Input
                    value={c.commentaire || ""}
                    onChange={e => updateCondition(c.condition_id, "commentaire", e.target.value)}
                    placeholder="Note ou commentaire..."
                    className="input-dark h-7 text-xs flex-1"
                    data-testid={`condition-${c.condition_id}-comment`}
                  />
                  {saving === c.condition_id && <Loader2 size={12} className="animate-spin text-orange-400" />}
                </div>

                {/* Document upload/display */}
                <div className="flex items-center gap-2">
                  {c.document_url ? (
                    <div className="flex items-center gap-2 flex-1 bg-zinc-800/60 rounded px-2 py-1">
                      <FileText size={14} className="text-orange-400 flex-shrink-0" />
                      <span className="text-zinc-300 text-xs truncate flex-1">{c.document_url.split("/").pop()}</span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${c.document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-400 hover:text-orange-400 transition-colors"
                        data-testid={`condition-${c.condition_id}-download`}
                      >
                        <Download size={14} />
                      </a>
                      <button
                        onClick={() => handleDeleteDoc(c.condition_id)}
                        className="text-zinc-400 hover:text-red-400 transition-colors"
                        data-testid={`condition-${c.condition_id}-delete-doc`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileRefs.current[c.condition_id]?.click()}
                      disabled={uploading === c.condition_id}
                      className="h-7 text-xs border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/30 gap-1"
                      data-testid={`condition-${c.condition_id}-upload`}
                    >
                      {uploading === c.condition_id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                      Justificatif
                    </Button>
                  )}
                  <input
                    ref={el => fileRefs.current[c.condition_id] = el}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={e => { handleUpload(c.condition_id, e.target.files[0]); e.target.value = ""; }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
